import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ElementRef,
  Renderer2,
  ChangeDetectorRef,
  ViewEncapsulation,
  ViewChild,
  HostBinding,
  Output,
  EventEmitter,
} from '@angular/core';
import { CaptureComponent } from '@modules/media/capture/capture.component';
import { CaptureModel } from '@modules/media/models/capture.model';
import { MediaStreamModel } from '@modules/media/models/media-stream.model';
import { VideoStatus } from '@modules/media/models/media-video.model';
import { TranscriptModel, TranscriptStatus } from '@modules/media/models/transcript.model';
import { AudioPlayState, AudioService } from '@modules/media/service/audio.service';
import { PlayerService } from '@modules/media/service/player.service';
import { TranscriptService } from '@modules/media/service/transcript.service';
import { VideoDefaultsComponent } from '@modules/media/video/video-defaults';
import { ContactService } from '@modules/shared/services/contact.service';
import { DataLayerService } from '@modules/shared/services/data-layer.service';
import { DialogService } from '@modules/shared/services/dialog.service';
import { LightboxService, LightboxState } from '@modules/shared/services/lightbox.service';
import { ModalService } from '@modules/shared/services/modal.service';
import { ShareModalService } from '@modules/shared/services/share.service';
import { StreamService } from '@modules/shared/services/stream.service';
import { ANALYTIC_EVENTS, TrackingService } from '@modules/shared/services/tracking.service';
import { Screen, WindowService } from '@modules/shared/services/window.service';
import { ToastService } from '@modules/toast/toast.service';
import { DynamicContainerComponent } from '@modules/ui/dynamic-container/dynamic-container.component';
import { ResizableContainerComponent } from '@modules/ui/resize-container/resizable-container.component';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'mb-stream-player',
  templateUrl: './stream-player.component.html',
  styleUrls: ['../media/video/video-defaults.scss', './stream-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class StreamPlayerComponent extends VideoDefaultsComponent {
  @HostBinding('class.transcript-open')
  public showTranscript = false;
  @HostBinding('class.thumbs-open')
  public thumbsOpen = false;
  @HostBinding('class.small-player')
  public smallPlayer = true;

  private statePoll$ = new Subject<boolean>();
  private transcriptInitialLangSet = false;
  private windowResizeListener: any = null;
  private wasPlayed = false;
  private rootNode = document.getElementsByTagName('html')[0];

  private readonly MEDIUM_SCREEN_WIDTH = 1024;

  public dimensions = { height: 0, width: 0 };
  public showCapture$ = new Subject<boolean>();
  public fillVideoHeight = false;
  public capture!: CaptureModel;

  public readonly videoBreakpoints = [0, this.MEDIUM_SCREEN_WIDTH];

  @ViewChild('buttonHub') buttonHub!: ElementRef;
  @ViewChild('dynamicWrapper') dynamicWrapper!: DynamicContainerComponent;
  @ViewChild('wrapperResizeContainer') wrapperResizeContainer!: ResizableContainerComponent;
  @Input() transcript!: TranscriptModel | null;
  @Input() allowPictureInPicture = true;
  @Input() stream!: MediaStreamModel;

  @HostBinding('class.embed-player')
  @Input()
  isEmbedPlayer = false;

  @Output() openMedia = new EventEmitter();
  @Output() streamStopped = new EventEmitter();
  @Output() shareStream = new EventEmitter();
  @Output() streamPlay = new EventEmitter();
  @Output() streamErrorData = new EventEmitter<{ errorText: string; showErrorScreen: boolean }>();

  constructor(
    public elRef: ElementRef,
    public playerService: PlayerService,
    public renderer: Renderer2,
    public cdr: ChangeDetectorRef,
    private toastService: ToastService,
    private transcriptService: TranscriptService,
    private windowService: WindowService,
    private audioService: AudioService,
    private translateService: TranslateService,
    private contactService: ContactService,
    shareService: ShareModalService,
    lightboxService: LightboxService,
    dialogService: DialogService,
    modalService: ModalService,
    private trackingService: TrackingService,
    private streamService: StreamService,
    private dataLayerService: DataLayerService
  ) {
    super(shareService, lightboxService, dialogService, modalService, cdr, playerService, elRef, renderer);
    this.windowResizeListener = this.calculateVideoDimensions.bind(this);
    window.addEventListener('resize', this.windowResizeListener);
    this.afterDestroyHook = () => {
      window.removeEventListener('resize', this.windowResizeListener);
      this.statePoll$.next(true);
      this.statePoll$.complete();

      this.rootNode.classList.remove('transcript-open');
      this.rootNode.classList.remove('multiangle-open');
    };
    this.afterInitHook = () => {
      const status = this.player.playerData.status;
      if (status === VideoStatus.LIVE) {
        this.pollForState();
      }

      this.wrapperWidthChanged(this.elRef.nativeElement.clientWidth);

      // emit play event for outer components (embed) to act on
      this.player.playing$.subscribe((isPlaying) => {
        if (isPlaying) {
          this.streamPlay.emit();
        }
      });
    };
    this.audioService.audioPlayState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.checkPlayOnOverlay(state === AudioPlayState.PLAY);
    });
    lightboxService.lightboxState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.checkPlayOnOverlay(
        state === LightboxState.OPEN &&
          !!lightboxService.activeResource &&
          lightboxService.activeResource.mediaType === 'video'
      );
    });
  }

  public showCapture(capture: CaptureModel): void {
    this.capture = capture;
    const key = this.toastService.open(
      {
        component: CaptureComponent,
        fullContent: true,
        whiteCloseBtn: true,
        componentData: {
          capture,
        },
        componentEvents: {
          captureClicked: () => this.toastService.removeToastByKey(key),
        },
      },
      this.elRef.nativeElement
    );
    this.showCapture$.next(true);

    this.dataLayerService.pushEvent({
      event: 'streamScreenshot',
      stream: this.getStreamTrackingItem(),
      frame: capture.getFrame(),
    });
  }

  public toggleTranscript(show: boolean): void {
    this.calculateMobileTranscriptHeight();
    const transcript = this.transcript as TranscriptModel;
    if (!this.transcriptInitialLangSet) {
      this.transcriptInitialLangSet = true;
      const applicationLanguage = this.translateService.currentLang.split('-')[0];
      // check if the transcript is available in the current application language
      if (transcript.languages.includes(applicationLanguage)) {
        transcript.lang = applicationLanguage;
      } else {
        // use default (which is master_language)
      }
    }
    this.showTranscript = show;
    if (show && !transcript.initializedEntries) {
      this.transcriptService.loadEntries(transcript);
    }
    if (show) {
      this.rootNode.classList.add('transcript-open');
      this.trackingService.push(location.href, {
        events: ANALYTIC_EVENTS.TRANSCRIPT_SHOWN,
      });
      this.dataLayerService.pushEvent({
        event: 'transcriptOpen',
        transcript: this.getTranscriptTrackingItem(),
        language: this.transcript!.lang,
      });
    } else {
      this.rootNode.classList.remove('transcript-open');
      this.trackingService.push(location.href, {
        events: ANALYTIC_EVENTS.TRANSCRIPT_HIDDEN,
      });
      this.dataLayerService.pushEvent({
        event: 'transcriptClose',
        transcript: this.getTranscriptTrackingItem(),
      });
    }
    this.cdr.detectChanges();
    this.calculateVideoDimensions();
  }

  public changeTranscriptLang(lang: string): void {
    if (this.transcript) {
      this.transcriptService.changeLanguage(this.transcript, lang);
    }
  }

  public wrapperWidthChanged(width: any): void {
    this.smallPlayer = width < this.MEDIUM_SCREEN_WIDTH;
    this.calculateMobileTranscriptHeight();
  }

  public thumbPositionUpdated(): void {
    if (this.smallPlayer) {
      this.toggleThumbs(false);
    }
    this.dataLayerService.pushEvent({
      event: 'streamCameraSelected',
      stream: this.getStreamTrackingItem(),
      cameraName: this.stream.playerConfig!.cameras![this.player.position].title,
    });
  }

  public toggleThumbs(open?: boolean): void {
    if (open === undefined) {
      open = !this.thumbsOpen;
    }
    this.thumbsOpen = open;
    if (open) {
      this.rootNode.classList.remove('multiangle-open');
      this.trackingService.push(location.href, {
        events: ANALYTIC_EVENTS.MULTI_ANGLE_VIEW,
      });
      this.dataLayerService.pushEvent({
        event: 'streamMultiAngleOpened',
        stream: this.getStreamTrackingItem(),
      });
    } else {
      this.rootNode.classList.remove('multiangle-open');
    }
    this.cdr.detectChanges();
  }

  public openShare(): void {
    if (this.isEmbedPlayer) {
      this.shareStream.emit();
      return;
    }
    this.shareService.openModal({
      shareData: {
        title: this.player.playerData.title,
        content: this.player.playerData.description,
        url: location.href,
      },
      trackingData: {
        id: this.stream.id,
        type: 'stream',
        title: this.stream.title,
        marsOriginId: undefined,
        shelfNumber: undefined,
      },
    });
  }

  public openQaForm(): void {
    const qaForm = this.player.playerData.qaForm;
    if (!qaForm) {
      return;
    }
    this.contactService.openQaForm(qaForm);
  }

  public calculateVideoDimensions(isScalingMobile = false): void {
    if (this.windowService.screen < Screen.SM) {
      this.dimensions = this.mobileVideoCalculation(isScalingMobile);
    } else {
      this.dimensions = this.desktopVideoCalculation();
    }
    this.calculateMobileTranscriptHeight();
    this.cdr.detectChanges();
  }

  public hideControls(): void {
    this.showControls$.next(false);
  }

  private pollForState(): void {
    this.streamService
      .pollPlayerStatusForStream(this.stream.id, this.statePoll$)
      .pipe(takeUntil(this.statePoll$))
      .subscribe((data) => {
        if (data) {
          const { errorText, showErrorScreen } = data;
          this.streamErrorData.emit({ errorText, showErrorScreen });
          this.player.playerData.status = data.status;
        }
        if (this.player.playerData.status === VideoStatus.STOPPED) {
          this.statePoll$.next(true);
          this.streamStopped.emit();
          if (this.transcript) {
            this.transcript.status = TranscriptStatus.FINISHED;
          }
        }
      });
  }

  private checkPlayOnOverlay(overlayOpened: boolean): void {
    if (this.player) {
      if (overlayOpened) {
        this.wasPlayed = this.player.playing;
        this.player.pause();
      } else {
        if (this.wasPlayed) {
          this.player.play();
        }
        this.wasPlayed = false;
      }
    }
  }

  private calculateMobileTranscriptHeight(): void {
    if (!this.dynamicWrapper) {
      return;
    }
    this.elRef.nativeElement.style.setProperty('--mobile-player-height', `${window.innerHeight}px`);
    this.elRef.nativeElement.style.setProperty(
      '--mobile-transcript-height',
      `${window.innerHeight - this.dynamicWrapper.elRef.nativeElement.clientHeight}px`
    );
  }

  private mobileVideoCalculation(isScalingMobile = false) {
    let headerHeight = 98;
    const isLandscape = window.innerHeight < window.innerWidth;
    if (this.showTranscript) {
      headerHeight = 0;
    }
    const el = this.elRef.nativeElement;
    const resizeContainerEl = this.wrapperResizeContainer.elRef.nativeElement;
    const hubHeight = this.buttonHub ? this.buttonHub.nativeElement.clientHeight : 0;

    const screenHeight = isLandscape ? window.innerHeight : window.innerHeight - (headerHeight + hubHeight);
    const ratio = this.media.ratio;
    const videoWrapper = this.videoParent.nativeElement;
    const parent = videoWrapper.parentNode;
    const videoWrapperWidth = parent.clientWidth;
    const totalMaxHeight = isLandscape
      ? window.innerHeight
      : isScalingMobile
      ? resizeContainerEl.clientHeight
      : videoWrapperWidth > screenHeight
      ? screenHeight
      : videoWrapperWidth;

    let dimensions = { width: videoWrapperWidth, height: Math.round(videoWrapperWidth / ratio) };

    if (dimensions.height > totalMaxHeight) {
      dimensions = {
        width: Math.round(totalMaxHeight * ratio),
        height: totalMaxHeight,
      };
    }
    if (!isScalingMobile) {
      resizeContainerEl.style.minHeight = (isLandscape ? dimensions.height : dimensions.height + 20) + 'px';
    } else {
      resizeContainerEl.style.minHeight = '';
    }
    el.style.height = '';

    return dimensions;
  }

  private desktopVideoCalculation() {
    let dimensions = { width: 0, height: 0 };
    const ratio = this.media.ratio;
    const videoWrapper = this.videoParent.nativeElement;
    const parent = videoWrapper.parentNode;

    const calculatedWidth = Math.round(parent.clientHeight * ratio);
    if (calculatedWidth <= parent.clientWidth) {
      dimensions = {
        width: calculatedWidth,
        height: parent.clientHeight,
      };
    } else {
      dimensions = {
        width: parent.clientWidth,
        height: Math.round(parent.clientWidth / ratio),
      };
    }

    return dimensions;
  }

  private getStreamTrackingItem() {
    return {
      id: this.stream.id,
      title: this.stream.title,
      status: this.stream.status,
      isMultiAngle: this.stream.video?.isComposed ?? false,
      hasTranscript: !!this.transcript,
    };
  }

  private getTranscriptTrackingItem() {
    return {
      id: this.transcript!.id,
      streamId: this.stream.id,
      title: this.transcript!.title,
      availableLanguages: this.transcript!.languages,
    };
  }
}
